import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Card,
  CardActionArea,
  CardContent,
  Collapse,
  Paper,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Helmet } from "react-helmet-async";
import { alpha } from "@mui/material/styles";
import { useMemo, useState } from "react";
import Seo from "../../../seo/Seo";

type QA = {
  id: string;
  question: string;
  answerText: string;
  answer?: React.ReactNode;
  category: "General" | "Eligibility" | "Payments" | "Support";
};

const SITE = process.env.REACT_APP_SITE_URL || "https://bondpay.au";

const RAW_FAQS: QA[] = [
  {
    id: "why-us",
    question: "Why us?",
    answerText:
      "No interest & no late fees for our BondPay solution. We understand the financial pressure of moving so we made BondPay affordable for renters.",
    category: "General",
  },
  {
    id: "fee",
    question: "What is your Fee?",
    answerText:
      "We charge a small admin fee $99 + first instalment of the bond upfront.",
    category: "Payments",
  },
  {
    id: "eligibility",
    question: "What do I need to be eligible to Apply?",
    answerText:
      "You simply need to be able to show a stable income for the past 90 days, Australian Citizen & 18 years or above.",
    category: "Eligibility",
  },
  {
    id: "credit-check",
    question: "Do you do credit checks?",
    answerText:
      "When you apply, we'll carry out a soft credit check to ensure you can afford the repayments without pressure. It won’t affect your credit score.",
    category: "Eligibility",
  },
  {
    id: "contact-agent",
    question: "Will you contact my agent?",
    answerText:
      "We'll contact your agent if we need to in order to process your application.",
    category: "General",
  },
  {
    id: "private-property",
    question:
      "Do you service private property that aren't managed by a real estate agent?",
    answerText:
      "Yes, we support private rentals. This is conditional upon the landlord lodging with the rental bond board in the respective state.",
    category: "General",
  },
  {
    id: "early-payment",
    question: "Can I make an early payment?",
    answerText:
      "You may repay the amount you owe in full at any time, without any fees or charges.",
    category: "Payments",
  },
  {
    id: "repay-time",
    question: "How long will I need to repay the Bond?",
    answerText:
      "We allow you to split your bond into 4 fortnightly instalments over 6 weeks for bonds up to $2000.",
    category: "Payments",
  },
  {
    id: "partial-bond",
    question: "Can you pay part of my Bond?",
    answerText:
      "Yes, we can fund the difference of the bond amount you require. If you need to do this, please email us on hello@bondpay.au before applying.",
    answer: (
      <>
        Yes, we can fund the difference of the bond amount you require. If you
        need to do this, please email us on{" "}
        <Link href="mailto:hello@bondpay.au" sx={{ color: "primary.main" }}>
          hello@bondpay.au
        </Link>{" "}
        before applying.
      </>
    ),
    category: "Payments",
  },
  {
    id: "cashback-admin",
    question: "Can I apply my cash back towards my admin fee?",
    answerText:
      "Yes, you can choose to apply your $60 cash back credit towards your admin fee. Please email us on hello@bondpay.au.",
    answer: (
      <>
        Yes, you can choose to apply your $60 cash back credit towards your
        admin fee. Please email us on{" "}
        <Link href="mailto:hello@bondpay.au" sx={{ color: "primary.main" }}>
          hello@bondpay.au
        </Link>
        .
      </>
    ),
    category: "Payments",
  },
  {
    id: "cashback-when",
    question: "When will I receive my cash back?",
    answerText:
      "Upon connection confirmation with our Utility partner we will transfer your earned funds into your nominated bank account within 3–5 business days.",
    category: "Payments",
  },
  {
    id: "contact",
    question: "How can I contact you?",
    answerText:
      "You can contact us by emailing Hello@bondpay.au. We respond within 24 hours; for urgent requests put URGENT in the subject line.",
    answer: (
      <>
        You can contact us by emailing{" "}
        <Link href="mailto:Hello@bondpay.au" sx={{ color: "primary.main" }}>
          Hello@bondpay.au
        </Link>
        . We respond within 24 hours; for urgent requests put <b>URGENT</b> in
        the subject line.
      </>
    ),
    category: "Support",
  },
];

function highlight(text: string, q: string) {
  if (!q.trim()) return text;
  try {
    const re = new RegExp(
      `(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "ig"
    );
    return text.replace(re, "<mark>$1</mark>");
  } catch {
    return text;
  }
}

export function Faq() {
  const theme = useTheme();
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<"All" | QA["category"]>("All");
  const [openId, setOpenId] = useState<string | null>(RAW_FAQS[0].id);

  const categories = [
    "All",
    "General",
    "Eligibility",
    "Payments",
    "Support",
  ] as const;

  const faqs = useMemo(() => {
    const base =
      cat === "All" ? RAW_FAQS : RAW_FAQS.filter((f) => f.category === cat);
    if (!query.trim()) return base;
    const q = query.toLowerCase();
    return base.filter(
      (f) =>
        f.question.toLowerCase().includes(q) ||
        f.answerText.toLowerCase().includes(q)
    );
  }, [cat, query]);

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: RAW_FAQS.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: { "@type": "Answer", text: q.answerText },
    })),
  };

  const breadcrumbsLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE },
      { "@type": "ListItem", position: 2, name: "FAQ", item: `${SITE}/faq` },
    ],
  };

  const heroBg = `radial-gradient(1200px 600px at 50% -10%, ${alpha(
    theme.palette.secondary.main,
    0.35
  )}, transparent 60%), linear-gradient(180deg, ${alpha(
    theme.palette.primary.light,
    0.18
  )} 0%, ${theme.palette.background.default} 60%)`;

  const cardBg = (active: boolean) =>
    `linear-gradient(#fff,#fff) padding-box, linear-gradient(135deg, ${
      active
        ? theme.palette.primary.main
        : alpha(theme.palette.primary.main, 0.4)
    }, ${theme.palette.secondary.main}) border-box`;

  return (
    <Box component="main">
      <Seo
        title="FAQ — BondPay"
        description="Everything about BondPay: eligibility, fees, repayments, credit checks, private rentals and support."
        path="/faq"
        type="website"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbsLd)}
        </script>
      </Helmet>

      <Box
        component="section"
        sx={{ py: { xs: 8, md: 12 }, textAlign: "center", background: heroBg }}
      >
        <Container maxWidth="md">
          <Typography
            component="h1"
            variant="h3"
            fontWeight={900}
            sx={{
              backgroundImage: `linear-gradient(30deg, ${theme.palette.secondary.main} 1%,  ${theme.palette.primary.main} 40%, ${theme.palette.primary.light} 60%)`,
              color: "transparent",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              mb: 1.5,
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography variant="subtitle1" color="text.primary" sx={{ mb: 3 }}>
            Everything you need to know about BondPay
          </Typography>

          <Paper
            sx={{
              mx: "auto",
              maxWidth: { xs: "100%", sm: 560, md: 720 },
              borderRadius: 999,
              overflow: "hidden",
            }}
            elevation={3}
          >
            <TextField
              fullWidth
              size="medium"
              variant="outlined"
              placeholder="Search the FAQ…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (openId && !RAW_FAQS.find((x) => x.id === openId))
                  setOpenId(null);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ pl: 1.5 }}>
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: { "& fieldset": { border: "none" }, px: 1 },
              }}
            />
          </Paper>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              mt: 2,
              justifyContent: "center",
              flexWrap: "wrap",
              rowGap: 1,
            }}
          >
            {categories.map((c) => (
              <Chip
                key={c}
                label={c}
                clickable
                color={cat === c ? "secondary" : "default"}
                variant={cat === c ? "filled" : "outlined"}
                onClick={() => setCat(c)}
              />
            ))}
          </Stack>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {faqs.map((item) => {
              const active = openId === item.id;
              return (
                <Grid key={item.id} item xs={12} sm={6} md={4}>
                  <Card
                    id={item.id}
                    sx={{
                      p: 1,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      // background: cardBg(active),
                      border: "1px solid transparent",
                      boxShadow: "0 0px 5px rgba(0,0,0,.4)",
                      transition:
                        "transform .2s ease, box-shadow .2s ease, background .2s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 16px 40px rgba(0,0,0,.12)",
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() =>
                        setOpenId((prev) => (prev === item.id ? null : item.id))
                      }
                      sx={{ borderRadius: 2 }}
                      aria-expanded={active}
                      aria-controls={`${item.id}-answer`}
                    >
                      <CardContent sx={{ pb: 1.5 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              background: `linear-gradient(0deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                              display: "grid",
                              placeItems: "center",
                            }}
                          >
                            <ExpandMoreIcon
                              sx={{
                                transform: active
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                transition: "transform .2s ease",
                                color: theme.palette.background.default,
                              }}
                            />
                          </Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight={800}
                            sx={{
                              lineHeight: 1.2,
                              "& mark": {
                                background: alpha(
                                  theme.palette.secondary.main,
                                  0.4
                                ),
                                borderRadius: 0.5,
                                px: 0.5,
                              },
                            }}
                            dangerouslySetInnerHTML={{
                              __html: highlight(item.question, query),
                            }}
                          />
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 0.75,
                            display: "inline-block",
                            color: "text.secondary",
                          }}
                        >
                          {item.category}
                        </Typography>
                      </CardContent>
                    </CardActionArea>

                    <Collapse in={active} unmountOnExit>
                      <CardContent id={`${item.id}-answer`} sx={{ pt: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            "& mark": {
                              background: alpha(
                                theme.palette.secondary.main,
                                0.35
                              ),
                              borderRadius: 0.5,
                              px: 0.5,
                            },
                          }}
                        >
                          {item.answer ?? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: highlight(item.answerText, query),
                              }}
                            />
                          )}
                        </Typography>
                      </CardContent>
                    </Collapse>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Paper
            elevation={0}
            sx={{
              mt: { xs: 4, md: 6 },
              p: { xs: 3, md: 4 },
              textAlign: "center",
              borderRadius: 3,
              bgcolor: alpha(theme.palette.secondary.light, 0.6),
              border: `1px solid ${alpha(theme.palette.secondary.dark, 0.25)}`,
            }}
          >
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
              Still have questions?
            </Typography>
            <Typography sx={{ mb: 0 }}>
              Drop us a line at{" "}
              <Link
                href="mailto:hello@bondpay.au"
                sx={{ color: "primary.main", fontWeight: 700 }}
              >
                hello@bondpay.au
              </Link>{" "}
              and we’ll get back within 24 hours.
            </Typography>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
